import { Button, Typography } from "@mui/material"
import { getFormattedDate } from "../../../../../utilities/helpers/DateTimeFormatters"
import { invoiceListStyles } from "../../InvoiceList/invoiceListStyles"
import doordash_image from '../../../../../assets/doordash_modal.png'
import { Language, SupportedLanguage } from "../../../../../language/LanguageTypes"
import CachedIcon from '@mui/icons-material/Cached'
import { RouteBuilderTab } from "../../../RouteBuilder"
import { isApac } from "../../../../../utilities/helpers/RegionHelper"
import { DeliveryRouteService } from "../../../../../utilities/services/DeliveryRouteService"
import { routeListStyles } from "../routeListStyles"
import Colors from "../../../../../assets/Colors"
import { DeliveryRoute } from "../../../../../utilities/types/DeliveryRouteTypes"

type RouteListColumnsProps = {
    language: Language
    currentLanguage: SupportedLanguage
    currentTab: RouteBuilderTab | null
    setSelectedDriverId: (value: string) => void
    setEditedRouteData: (value: DeliveryRoute | null) => void
    setRoute: (value: DeliveryRoute | null) => void
    setCompletedRouteData: (completedRouteData: DeliveryRoute[], routeStatus: string) => void
    setOpenCompletedRoute: (value: boolean) => void
    setOpenEditRoute: (value: boolean) => void
}

const renderAutoDispatchedColumn = (currentTab: RouteBuilderTab | null, language: Language, currentLanguage: SupportedLanguage) => {
    return (
        <Typography sx={invoiceListStyles.autoDispatch}>
            {currentTab === 'routes' && (
                <Typography data-testid="autodash" sx={invoiceListStyles.autoDispatchText}>
                    <CachedIcon data-testid="CachedIcon" />
                    <Typography sx={invoiceListStyles.autoDispatchColumnText}>
                        {language[currentLanguage].autoDispatchColumn}
                    </Typography>
                </Typography>
            )}
        </Typography>
    )
}

export const getRouteListColumns = ({
    language,
    currentLanguage,
    currentTab,
    setSelectedDriverId,
    setEditedRouteData,
    setRoute,
    setCompletedRouteData,
    setOpenCompletedRoute,
    setOpenEditRoute
}: RouteListColumnsProps) =>
    [
        {
            field: 'routeId',
            headerName: language[currentLanguage].routeNumber.toUpperCase(),
            minWidth: 250,
            editable: false,
            valueFormatter: (params: any) => {
                return `#${params.value}`
            }
        },
        {
            field: 'driver',
            headerName: language[currentLanguage].driver.toUpperCase(),
            minWidth: 250,
            editable: false,
            renderCell: (params: any) => {
                if (params.row.driver.includes('DOORDASH'))
                    return <img src={doordash_image} alt="" width="124px" height="24px" />
                else return params.row.driver
            }
        },
        {
            field: 'lastUpdated',
            headerName: language[currentLanguage].lastUpdatedHeader,
            type: 'date',
            filterable: false,
            valueFormatter: (params: any) => {
                return getFormattedDate(params.value)
            },
            minWidth: 250,
            editable: false
        },
        {
            field: 'status',
            headerName: language[currentLanguage].status.toUpperCase(),
            valueFormatter: (params: any) => {
                return params.value?.includes('Delayed') || params.value?.includes('Dasher Confirmed') ? params.value : params.value?.slice(2)
            },
            minWidth: 250,
            editable: false
        },
        {
            field: 'autoDispatchTime',
            headerName: language[currentLanguage].autoDispatching,
            renderCell: (params: any) => {
                return params.row.autoDispatch ? renderAutoDispatchedColumn(currentTab, language, currentLanguage) : ''
            },
            filterable: false,
            minWidth: 250,
            editable: false
        },
        {
            field: 'date',
            headerName: language[currentLanguage].dateText,
            minWidth: 250,
            filterable: false,
            editable: false
        },
        {
            field: 'customer',
            headerName: language[currentLanguage].customerText,
            minWidth: 250,
            editable: false
        },
        {
            field: 'invoiceNumber',
            headerName: 'INVOICE NUMBER',
            minWidth: 250,
            editable: false
        },
        {
            field: 'partNumber',
            headerName: 'PART NUMBER',
            minWidth: 250,
            editable: false
        },
        {
            field: 'edit',
            headerName: '',
            minWidth: 250,
            editable: false,
            filterable: false,
            renderCell: (params: any) => {
                const isUpdateInProgressEnabled: boolean = !isApac
                return params.row.status === 'B-Pending' || params.row.status === 'D-Created' || (params.row.status === 'A-En Route' && isUpdateInProgressEnabled) || (params.row.status === 'Delayed' && isUpdateInProgressEnabled) || params.row.status === 'Dasher Confirmed' ? (
                    <Button
                        sx={routeListStyles.editRouteButton}
                        onClick={async () => {
                            const routeData: DeliveryRoute = await DeliveryRouteService.getRouteByRouteId(params.row.routeId)
                            // Moved complete Modal to open after the route data is set
                            if (routeData) {
                                setOpenEditRoute(true)
                                if (!routeData?.deliveryDriver) {
                                    setSelectedDriverId('None')
                                } else {
                                    setSelectedDriverId(routeData.deliveryDriver)
                                }
                                setEditedRouteData(routeData)
                                setRoute(routeData)
                            }
                        }}>
                        {language[currentLanguage].editRoute}
                    </Button>
                ) : params.row.status === 'C-Delivered' || (params.row.status === 'A-En Route' && !isUpdateInProgressEnabled) || (params.row.status === 'Delayed' && !isUpdateInProgressEnabled) ? (
                    <Button
                        sx={{ textTransform: 'none', color: Colors.napaBlueLink }}
                        onClick={async () => {
                            const routeData = await DeliveryRouteService.getRouteByRouteId(params.row.routeId)
                            // Moved complete Modal to open after the route data is set
                            if (routeData) {
                                setCompletedRouteData([routeData], params.row.status)
                                setOpenCompletedRoute(true)
                            }
                        }}>
                        {language[currentLanguage].viewDetails}
                    </Button>
                ) : null
            }
        }
    ]